/**
 * « Mes sociétés » (PLAN-MULTI-TENANT § 4.3, DM23, lot BS): the links a person keeps to the other companies they are a
 * member of. Each company is a cell, with its own base, its own sign-in and its own address: a link opens the other
 * cell's application, which the person signs in to with the same address. The base keeps the links to their person
 * alone (`my_company_links`) and fills the person itself: nothing here sends it.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { z } from 'zod';

import { logError } from './logger';
import { supabase } from './supabase';

// my_company_links is newer than src/types/supabase.ts, which is not regenerated for it: its rows are validated below.
const db = supabase as unknown as SupabaseClient;

export const COMPANY_LABEL_MAX = 60;
const URL_MAX = 2048;

const linkSchema = z.object({ id: z.string().uuid(), label: z.string(), url: z.string() });

export type CompanyLink = Readonly<z.infer<typeof linkSchema>>;

/** What a write can refuse, in French, for the screen. */
export const COMPANY_LINK_MESSAGES = {
  label: `Donnez un nom à la société (${COMPANY_LABEL_MAX} caractères au plus).`,
  url: 'Saisissez l’adresse de l’application de cette société, en https (par exemple https://societe.exemple.fr).',
  duplicate: 'Ce lien est déjà dans votre liste.',
  saveFailed: 'Le lien n’a pas pu être enregistré. Réessayez dans un instant.',
  removeFailed: 'Le lien n’a pas pu être retiré. Réessayez dans un instant.',
  readFailed: 'Vos sociétés n’ont pas pu être lues. Réessayez dans un instant.',
} as const;

/**
 * An address typed by the person, as the base keeps it: https, the host in lower case, no name or password in it; a
 * missing scheme reads as https. Null for anything else. Pure.
 */
export function normalizeCompanyUrl(input: string): string | null {
  const text = input.trim();
  if (!text) return null;
  let url: URL;
  try {
    url = new URL(/^[a-z][a-z0-9+.-]*:/i.test(text) ? text : `https://${text}`);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' || url.username || url.password || !url.hostname.includes('.')) return null;
  return url.href.length <= URL_MAX ? url.href : null;
}

/** A name as the base keeps it (trimmed, 1 to 60 characters), or null. Pure. */
export function normalizeCompanyLabel(input: string): string | null {
  const label = input.trim();
  return label.length >= 1 && label.length <= COMPANY_LABEL_MAX ? label : null;
}

/** Whether a link opens the application the person is in (same origin). Pure. */
export function isCurrentCompany(link: CompanyLink, origin: string): boolean {
  try {
    return new URL(link.url).origin === origin;
  } catch {
    return false;
  }
}

/** The links a menu offers: those that open another application than the one the person is in. Pure. */
export function linksToOtherCompanies(links: readonly CompanyLink[], origin: string): CompanyLink[] {
  return links.filter((link) => !isCurrentCompany(link, origin));
}

/** The links in the order of the menu: by name, as a French reader sorts them. Pure. */
export function sortCompanyLinks(links: readonly CompanyLink[]): CompanyLink[] {
  return [...links].sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
}

/** The rows of the base, validated; a row of another shape is left out. Pure. */
export function parseCompanyLinks(raw: unknown): CompanyLink[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((row) => {
    const parsed = linkSchema.safeParse(row);
    return parsed.success ? [parsed.data] : [];
  });
}

/** The signed-in person's links. Throws on a transport or database error. */
export async function fetchCompanyLinks(): Promise<CompanyLink[]> {
  const { data, error } = await db.from('my_company_links').select('id, label, url');
  if (error) throw new Error(error.message);
  return sortCompanyLinks(parseCompanyLinks(data));
}

/** The answer of a write: the link written, or why it was refused (a French message). */
export type CompanyLinkWrite = { readonly ok: true; readonly link: CompanyLink } | { readonly ok: false; readonly message: string };

/** Writes a link for the signed-in person (the base makes it theirs). Never throws. */
export async function insertCompanyLink(label: string, url: string): Promise<CompanyLinkWrite> {
  const { data, error } = await db.from('my_company_links').insert({ label, url }).select('id, label, url').single();
  if (error?.code === '23505') return { ok: false, message: COMPANY_LINK_MESSAGES.duplicate };
  if (error) {
    logError('companies', 'my_company_links insert failed', new Error(error.message), { code: error.code });
    return { ok: false, message: COMPANY_LINK_MESSAGES.saveFailed };
  }
  const [link] = parseCompanyLinks([data]);
  return link ? { ok: true, link } : { ok: false, message: COMPANY_LINK_MESSAGES.saveFailed };
}

/** Removes one of the signed-in person's links. Throws on a transport or database error. */
export async function deleteCompanyLink(id: string): Promise<void> {
  const { error } = await db.from('my_company_links').delete().eq('id', id);
  if (error) throw new Error(error.message);
}
